import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import { snapEarnService, ApiError } from '../services';
import moment from 'moment';

const OneSnapTransactionsPage = () => {
  const navigate = useNavigate();
  const hasInitialLoadRef = useRef(false);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchShopper, setSearchShopper] = useState('');
  const [searchShopperFocused, setSearchShopperFocused] = useState(false);
  const [dateType, setDateType] = useState('transactionDate'); // 'transactionDate' or 'submissionDate'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // State for API data
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  // State for jump to page input
  const [jumpToPage, setJumpToPage] = useState('');
  const [jumpError, setJumpError] = useState(false);

  // Rows per page options
  const rowsPerPageOptions = [10, 20, 50, 100];

  // Fetch transactions from API
  const fetchTransactions = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        page: params.page || 1,
        size: params.size || 20,
      };

      if (params.keyword && params.keyword.length >= 3) {
        requestParams.keyword = params.keyword;
      }

      if (params.shopperName && params.shopperName.length >= 3) {
        requestParams.shopperName = params.shopperName;
      }

      if (params.dateFilter) {
        requestParams.dateFilter = params.dateFilter;
        if (params.dateType) {
          requestParams.dateType = params.dateType;
        }
      }
      if (params.status) {
        requestParams.status = params.status;
      }
      const response = await snapEarnService.getFormattedTransactionList(requestParams);

      if (response && response.data) {
        setTransactions(response.data.data || []);
        setPagination({
          page: response.data.page || 1,
          pageCount: response.data.pageCount || 1,
          size: response.data.size || 20,
          count: response.data.count || 0,
          total: response.data.total || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch snap earn transactions:', err);

      if (err instanceof ApiError) {
        setError(`Failed to load transactions: ${err.message}`);
      } else {
        setError('Failed to load transactions. Please try again.');
      }

      setTransactions([]);
      setPagination(prev => ({ ...prev, count: 0, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      fetchTransactions({ page: 1, size: 20 });
    }
  }, [fetchTransactions]);

  // Handle search input
  const handleSearchFocus = useCallback(() => {
    setSearchFocused(true);
  }, []);

  const handleSearchChange = useCallback(e => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setSearchFocused(false);
  }, []);

  // Handle search shopper input
  const handleSearchShopperFocus = useCallback(() => {
    setSearchShopperFocused(true);
  }, []);

  const handleSearchShopperChange = useCallback((e) => {
    setSearchShopper(e.target.value);
  }, []);

  const handleSearchShopperBlur = useCallback(() => {
    setSearchShopperFocused(false);
  }, []);

  // Handle status filter change
  const handleStatusChange = useCallback(e => {
    setStatusFilter(e.target.value);
  }, []);

  // Handle date type change
  const handleDateTypeChange = useCallback((e) => {
    setDateType(e.target.value);
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    const requestParams = {
      page: 1,
      size: pagination.size,
    };

    if (searchTerm && searchTerm.length >= 3) {
      requestParams.keyword = searchTerm;
    }

    if (searchShopper && searchShopper.length >= 3) {
      requestParams.shopperName = searchShopper;
    }

    if (startDate && endDate) {
      // Format dates to 'YYYY-MM-DD'
      requestParams.dateFilter = {
        start: moment(startDate).format('YYYY-MM-DD'),
        end: moment(endDate).format('YYYY-MM-DD'),
      };
      requestParams.dateType = dateType;
    }

    if (statusFilter) {
      requestParams.status = statusFilter;
    }
    fetchTransactions(requestParams);
  }, [searchTerm, searchShopper, startDate, endDate, dateType, statusFilter, fetchTransactions, pagination.size]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSearchShopper('');
    setDateType('transactionDate');
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    fetchTransactions({ page: 1, size: pagination.size });
  }, [fetchTransactions, pagination.size]);

  // Handle page change
  const handlePageChange = useCallback(
    newPage => {
      const requestParams = {
        page: newPage,
        size: pagination.size,
      };

      if (searchTerm && searchTerm.length >= 3) {
        requestParams.keyword = searchTerm;
      }

      if (searchShopper && searchShopper.length >= 3) {
        requestParams.shopperName = searchShopper;
      }

      if (startDate && endDate) {
        // Format dates to 'YYYY-MM-DD'
        requestParams.dateFilter = {
          start: moment(startDate).format('YYYY-MM-DD'),
          end: moment(endDate).format('YYYY-MM-DD'),
        };
        requestParams.dateType = dateType;
      }

      if (statusFilter) {
        requestParams.status = statusFilter;
      }

      fetchTransactions(requestParams);
    },
    [searchTerm, searchShopper, startDate, endDate, dateType, statusFilter, fetchTransactions, pagination.size]
  );

  // Handle jump to specific page
  const handleJumpToPage = useCallback(() => {
    const pageNumber = parseInt(jumpToPage, 10);
    if (pageNumber && pageNumber >= 1 && pageNumber <= pagination.pageCount) {
      handlePageChange(pageNumber);
      setJumpToPage('');
      setJumpError(false);
    } else if (jumpToPage) {
      setJumpError(true);
      setTimeout(() => setJumpError(false), 2000);
    }
  }, [jumpToPage, pagination.pageCount, handlePageChange]);

  // Handle jump to page input change
  const handleJumpInputChange = useCallback((e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setJumpToPage(value);
      setJumpError(false);
    }
  }, []);

  // Handle Enter key press on jump input
  const handleJumpKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  }, [handleJumpToPage]);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((newSize) => {
    const requestParams = {
      page: 1, // Reset to first page when changing size
      size: newSize,
    };

    if (searchTerm && searchTerm.length >= 3) {
      requestParams.keyword = searchTerm;
    }

    if (searchShopper && searchShopper.length >= 3) {
      requestParams.shopperName = searchShopper;
    }

    if (startDate && endDate) {
      requestParams.dateFilter = {
        start: moment(startDate).format('YYYY-MM-DD'),
        end: moment(endDate).format('YYYY-MM-DD'),
      };
      requestParams.dateType = dateType;
    }

    if (statusFilter) {
      requestParams.status = statusFilter;
    }

    fetchTransactions(requestParams);
  }, [searchTerm, searchShopper, startDate, endDate, dateType, statusFilter, fetchTransactions]);

  // Generate page numbers with ellipsis
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const totalPages = pagination.pageCount;
    const currentPage = pagination.page;

    if (totalPages <= 7) {
      // Show all pages if 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [pagination.page, pagination.pageCount]);

  // Calculate showing range
  const showingFrom = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.size + 1;
  const showingTo = Math.min(pagination.page * pagination.size, pagination.total);

  // Format amount with . as decimal separator
  const formatAmount = amount => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Get status badge style
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle action - navigate to detail page
  const handleViewDetail = transaction => {
    navigate(`/transactions/1-snap/${transaction.id}`, {
      state: { transaction },
    });
  };

  const handleExportCSV = () => {
    console.log('Export to CSV');
    // TODO: Implement CSV export
  };

  return (
    <div>
      <PageHeader
        title='Snap & Earn'
        subtitle='Manage your transactions'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Snap & Earn' },
        ]}
      />

      {/* Search and Filter Section */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6'>
        {/* First Row: Search Transaction, Search Shopper, Status, Clear, Search */}
        <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4'>
          {/* Search Transaction */}
          <div className='relative'>
            <label
              htmlFor='search'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search Transaction
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='search'
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder='Search by company name or reference...'
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-md text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${searchFocused ? 'border-blue-300' : 'border-gray-300'}
                  ${searchTerm.length > 0 && searchTerm.length < 3
                    ? 'border-yellow-300 bg-yellow-50'
                    : ''
                  }
                `}
              />
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <div className='absolute right-3 top-2'>
                  <div className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded'>
                    {3 - searchTerm.length} more
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Shopper */}
          <div className='relative'>
            <label
              htmlFor='searchShopper'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search Shopper
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='searchShopper'
                value={searchShopper}
                onChange={handleSearchShopperChange}
                onFocus={handleSearchShopperFocus}
                onBlur={handleSearchShopperBlur}
                placeholder='Search by shopper name or phone...'
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-md text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${searchShopperFocused ? 'border-blue-300' : 'border-gray-300'}
                  ${searchShopper.length > 0 && searchShopper.length < 3
                    ? 'border-yellow-300 bg-yellow-50'
                    : ''
                  }
                `}
              />
              {searchShopper.length > 0 && searchShopper.length < 3 && (
                <div className='absolute right-3 top-2'>
                  <div className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded'>
                    {3 - searchShopper.length} more
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label
              htmlFor='statusFilter'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              All Status
            </label>
            <select
              id='statusFilter'
              value={statusFilter}
              onChange={handleStatusChange}
              className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value=''>All Status</option>
              <option value='A'>Approved</option>
              <option value='P'>Pending</option>
              <option value='R'>Rejected</option>
            </select>
          </div>

          {/* Clear Button */}
          <div className='flex flex-col justify-end'>
            <button
              onClick={clearFilters}
              className='w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500'
            >
              Clear
            </button>
          </div>

          {/* Search Button */}
          <div className='flex flex-col justify-end'>
            <button
              onClick={applyFilters}
              disabled={loading}
              className='w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div >

        {/* Second Row: Date Type and Date Range */}
        < div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4' >
          {/* Date Type Dropdown */}
          < div >
            <label
              htmlFor='dateType'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Data Type
            </label>
            <select
              id='dateType'
              value={dateType}
              onChange={handleDateTypeChange}
              className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            >
              <option value='transactionDate'>Transaction Date</option>
              <option value='submissionDate'>Submission Date</option>
            </select>
          </div >

          {/* Date Range */}
          < div className='lg:col-span-2' >
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onDateRangeChange={({ startDate: start, endDate: end }) => {
                handleDateRangeChange(start, end);
              }}
              placeholder={`Select ${dateType === 'transactionDate' ? 'transaction' : 'submission'} date range`}
              label={{
                start: 'Date From',
                end: 'Date To',
              }}
            />
          </div >
        </div >
      </div >

      {/* Error Display */}
      {
        error && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <div className='flex items-center'>
              <svg
                className='w-5 h-5 text-red-400 mr-2'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
              <p className='text-sm text-red-700'>{error}</p>
              <button
                onClick={() => setError(null)}
                className='ml-auto text-red-400 hover:text-red-600'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          </div>
        )
      }

      {/* Transactions Table */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
          <div>
            <h3 className='text-lg font-medium text-gray-900 flex items-center gap-2'>
              Transactions List
              {loading && (
                <svg
                  className='animate-spin h-4 w-4 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h3>
            <p className='text-sm text-gray-500 mt-1'>
              Showing {(pagination.page - 1) * pagination.size + 1} to{' '}
              {Math.min(pagination.page * pagination.size, pagination.total)} of{' '}
              {pagination.total} transactions
            </p>
          </div>
          {/* <button
            onClick={handleExportCSV}
            className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            Export to CSV
          </button> */}
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Company Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Sector
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Outlet
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Shopper Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Shopper Phone Number
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Date & Time
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Submission Date & Time
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Amount
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {transactions.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan='9'
                    className='px-6 py-8 text-center text-gray-500'
                  >
                    <div className='flex flex-col items-center'>
                      <svg
                        className='w-12 h-12 text-gray-300 mb-3'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                      <p className='text-lg font-medium'>
                        No transactions found
                      </p>
                      <p className='text-sm'>
                        Try adjusting your search criteria or check back later.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                transactions.map((transaction, index) => (
                  <tr
                    key={transaction.id || index}
                    className='hover:bg-gray-50'
                  >
                    <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.companyName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap'>
                      <span className='inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800'>
                        {transaction.sector}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-normal break-words max-w-xs'>
                      {transaction.outlet}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.shopperName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.shopperPhone}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.transactionDateTime
                        ? moment(transaction.transactionDateTime).format(
                          'D MMM YYYY HH:mm'
                        )
                        : 'N/A'}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {transaction.submissionDateTime ? moment(transaction.submissionDateTime).format(
                        'D MMM YYYY HH:mm'
                      ) : 'N/A'}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {formatAmount(transaction.transactionAmount)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyle(
                          transaction.status
                        )}`}
                      >
                        {transaction.status}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center'>
                      <button
                        onClick={() => handleViewDetail(transaction)}
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium inline-flex items-center gap-1'
                      >
                        <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination UI - Improved */}
        {pagination.total > 0 && (
          <div className='px-4 md:px-6 py-4 border-t border-gray-200 bg-gray-50/50'>
            {/* Mobile view */}
            <div className='flex flex-col gap-4 lg:hidden'>
              {/* Showing info & Rows per page */}
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
                <p className='text-sm text-gray-600'>
                  Showing <span className='font-semibold text-gray-900'>{showingFrom.toLocaleString()}</span> - <span className='font-semibold text-gray-900'>{showingTo.toLocaleString()}</span> of <span className='font-semibold text-gray-900'>{pagination.total.toLocaleString()}</span> records
                </p>
                <div className='flex items-center gap-2'>
                  <label className='text-sm text-gray-600'>Rows:</label>
                  <select
                    value={pagination.size}
                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                    disabled={loading}
                    className='px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50'
                  >
                    {rowsPerPageOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Navigation buttons */}
              <div className='flex items-center justify-between'>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                  className='inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
                  </svg>
                  Prev
                </button>
                <div className='flex items-center gap-1'>
                  <span className='px-3 py-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-lg'>
                    {pagination.page}
                  </span>
                  <span className='text-sm text-gray-500'>of {pagination.pageCount}</span>
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pageCount || loading}
                  className='inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Next
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                  </svg>
                </button>
              </div>

              {/* Jump to Page - Mobile */}
              <div className='flex items-center justify-center gap-2 p-3 bg-white rounded-lg border border-gray-200'>
                <span className='text-sm text-gray-600'>Jump to page:</span>
                <div className='relative'>
                  <input
                    type='text'
                    inputMode='numeric'
                    value={jumpToPage}
                    onChange={handleJumpInputChange}
                    onKeyPress={handleJumpKeyPress}
                    placeholder='#'
                    className={`w-16 px-2 py-1.5 text-sm text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                      jumpError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {jumpError && (
                    <div className='absolute -bottom-5 left-0 right-0 text-xs text-red-500 text-center'>
                      Invalid page
                    </div>
                  )}
                </div>
                <button
                  onClick={handleJumpToPage}
                  disabled={loading || !jumpToPage}
                  className='px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Go
                </button>
              </div>
            </div>

            {/* Desktop view */}
            <div className='hidden lg:flex lg:items-center lg:justify-between'>
              {/* Left side - Showing info & Rows per page */}
              <div className='flex items-center gap-6'>
                <p className='text-sm text-gray-600'>
                  Showing <span className='font-semibold text-gray-900'>{showingFrom.toLocaleString()}</span> - <span className='font-semibold text-gray-900'>{showingTo.toLocaleString()}</span> of <span className='font-semibold text-gray-900'>{pagination.total.toLocaleString()}</span> records
                </p>
                <div className='flex items-center gap-2 pl-6 border-l border-gray-300'>
                  <label className='text-sm text-gray-600'>Rows per page:</label>
                  <select
                    value={pagination.size}
                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                    disabled={loading}
                    className='px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 cursor-pointer'
                  >
                    {rowsPerPageOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right side - Navigation */}
              <div className='flex items-center gap-4'>
                {/* Jump to Page */}
                <div className='flex items-center gap-2 pr-4 border-r border-gray-300'>
                  <span className='text-sm text-gray-600'>Go to:</span>
                  <div className='relative'>
                    <input
                      type='text'
                      inputMode='numeric'
                      value={jumpToPage}
                      onChange={handleJumpInputChange}
                      onKeyPress={handleJumpKeyPress}
                      placeholder={pagination.page.toString()}
                      className={`w-14 px-2 py-1.5 text-sm text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                        jumpError ? 'border-red-400 bg-red-50' : 'border-gray-300'
                      }`}
                      disabled={loading}
                    />
                  </div>
                  <button
                    onClick={handleJumpToPage}
                    disabled={loading || !jumpToPage}
                    className='px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    Go
                  </button>
                  {jumpError && (
                    <span className='text-xs text-red-500'>1-{pagination.pageCount}</span>
                  )}
                </div>

                {/* Page Navigation */}
                <nav className='flex items-center gap-1' aria-label='Pagination'>
                  {/* First & Previous */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.page <= 1 || loading}
                    className='p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors'
                    title='First page'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 19l-7-7 7-7m8 14l-7-7 7-7' />
                    </svg>
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                    className='p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors'
                    title='Previous page'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
                    </svg>
                  </button>

                  {/* Page numbers with ellipsis */}
                  <div className='flex items-center gap-1 mx-1'>
                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className='px-2 py-1 text-sm text-gray-400'>
                          •••
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                          className={`min-w-[36px] px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                            page === pagination.page
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {page}
                        </button>
                      )
                    ))}
                  </div>

                  {/* Next & Last */}
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pageCount || loading}
                    className='p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors'
                    title='Next page'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.pageCount)}
                    disabled={pagination.page >= pagination.pageCount || loading}
                    className='p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors'
                    title='Last page'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 5l7 7-7 7M5 5l7 7-7 7' />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default OneSnapTransactionsPage;
